import React, { useEffect, useRef, useState } from "react";
import { pausableInterval } from "@/lib/appVisibility";
import { Link, useNavigate } from "react-router-dom";
import { Users, Radio } from "lucide-react";
import { useDeferredAutoplay } from "@/lib/useDeferredAutoplay";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { HlsPlayer, WhepPlayer } from "@/components/LiveRoom";
import { resolveMediaUrl } from "@/lib/mediaUrl";
import { liveTitle } from "@/lib/liveTitle";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { avatarSrc } from "@/lib/defaults";
import { DismissX } from "@/components/smart/DismissX";
import { useGuard } from "@/lib/guards";

// Lot 668 — direct en cours affiché sur le profil du membre (aperçu muet, comme Facebook) ;
// un tap ouvre la salle du direct. Rafraîchi toutes les 30 s ; disparaît dès la fin du direct.
// Lot 669 — même carte en tête du fil quand un membre SUIVI est en direct (`host` → en-tête avatar + nom + « Suivi(e) »,
// `poll={false}` : la liste des directs du fil se rafraîchit déjà, `onDismiss` → « × » pour la session).
// Lot 678 — allégée : l'image du direct s'affiche d'abord ; le lecteur muet n'est monté que si la carte est bien visible
// 0,8 s (useDeferredAutoplay) et il est démonté (flux libéré) dès qu'on défile ou que l'app passe en arrière-plan.
export const ProfileLiveCard = ({ live, hostName, onEnded, host = null, poll = true, onDismiss = null, testId = "profile-live-card" }) => {
  const { t } = useApp();
  const nav = useNavigate();
  const [lv, setLv] = useState(live);
  const enabled = useGuard("live_preview_cards"); // lot 671 — garde-fou admin (anneau + badge du profil non concernés)
  const stageRef = useRef(null);
  const playing = useDeferredAutoplay(stageRef, { enabled });
  useEffect(() => { setLv(live); }, [live]);
  useEffect(() => {
    if (!live?.id || !poll) return undefined;
    let alive = true;
    const tick = () => api.get(`/live/${live.id}`).then(({ data }) => {
      if (!alive) return;
      if (data.live?.status !== "live") onEnded?.(live.id); else setLv((p) => ({ ...p, ...data.live }));
    }).catch(() => {});
    const stop = pausableInterval(tick, 30000, { onResume: false }); // lot 699 — repos en arrière-plan
    return () => { alive = false; stop(); };
  }, [live?.id, poll]);  // eslint-disable-line react-hooks/exhaustive-deps
  if (!lv?.id || !enabled) return null;
  const open = () => nav(`/live?watch=${lv.id}`);
  const useWhep = lv.ingest === "webrtc" && !!lv.whep_url;
  const poster = lv.poster_url || lv.thumbnail_url ? resolveMediaUrl(lv.poster_url || lv.thumbnail_url) : undefined;
  const name = hostName || host?.name || "";
  return (
    <div className="yalola-card overflow-hidden" data-testid={testId}>
      {host && (
        <div className="px-4 pt-3 pb-2 flex items-center gap-3">
          <Link to={`/profile/${host.id}`} className="flex items-center gap-3 min-w-0 flex-1" data-testid={`${testId}-host`}>
            <span className="rounded-full p-[2px] shrink-0" style={{ background: "conic-gradient(from 180deg, #EF4444, #fb7185, #EF4444)" }}>
              <Avatar className="h-10 w-10 ring-2" style={{ "--tw-ring-color": "var(--yalola-surface)" }}>
                <AvatarImage src={avatarSrc(host)} />
                <AvatarFallback>{name?.[0]}</AvatarFallback>
              </Avatar>
            </span>
            <span className="min-w-0">
              <span className="block font-bold text-sm truncate">{name}</span>
              <span className="block text-xs truncate" style={{ color: "var(--yalola-text-muted)" }}>{t("feed.live_followed_hint", "Membre suivi · en direct maintenant")}</span>
            </span>
          </Link>
          {onDismiss && <DismissX onClick={onDismiss} testId={`${testId}-dismiss`} label={t("smart_cards.dismiss", "Masquer")} />}
        </div>
      )}
      <button ref={stageRef} type="button" onClick={open} className="relative block w-full aspect-video bg-black text-left outline-none overflow-hidden focus-visible:ring-2 focus-visible:ring-[var(--yalola-primary)]" aria-label={t("profile.live_open", "Regarder le direct")} data-testid="profile-live-video" data-playing={playing ? "1" : "0"}>
        {/* Image du direct tant que le lecteur n'est pas lancé (et à sa place si réseau lent / économiseur de données). */}
        {!playing && poster && <img src={poster} alt="" className="absolute inset-0 h-full w-full object-cover" decoding="async" data-testid="profile-live-poster" />}
        {playing && (useWhep
          ? <WhepPlayer src={lv.whep_url} muted testId="profile-live-player" />
          : lv.hls_url ? <HlsPlayer src={lv.hls_url} muted controls={false} poster={poster} testId="profile-live-player" compact /> : null)}
        <div className="absolute top-3 left-3 flex items-center gap-2 z-[8]">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-extrabold tracking-wide text-white" style={{ background: "#EF4444", boxShadow: "0 2px 8px rgba(239,68,68,0.45)" }} data-testid="profile-live-badge">
            <span className="h-2 w-2 rounded-full bg-white animate-pulse" /> {t("profile.live_badge", "LIVE")}
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold text-white" style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(8px)" }} data-testid="profile-live-viewers">
            <Users size={12} /> {lv.viewers || 0}
          </span>
        </div>
        <div className="absolute inset-x-0 bottom-0 p-3 pt-10 z-[8]" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.75), rgba(0,0,0,0))" }}>
          <div className="text-white font-bold text-sm leading-snug line-clamp-2" data-testid="profile-live-title">{liveTitle(lv, t, { name, ended: false })}</div>
        </div>
      </button>
      <div className="px-4 py-3 flex items-center justify-between gap-3">
        <div className="text-sm font-semibold" style={{ color: "var(--yalola-text-muted)" }}>{t("profile.live_now_hint", "{name} est en direct en ce moment").replace("{name}", name.split(" ")[0] || name)}</div>
        <button type="button" onClick={open} className="shrink-0 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold text-white transition-transform hover:scale-[1.03]" style={{ background: "#EF4444" }} data-testid="profile-live-join">
          <Radio size={15} /> {t("profile.live_join", "Rejoindre")}
        </button>
      </div>
    </div>
  );
};

export default ProfileLiveCard;
